import { ChangeDetectorRef, Component, inject, OnInit, signal } from '@angular/core';
import { ConfirmDialogComponent } from '../../main/confirm-dialog/confirm-dialog.component';
import { ProviderService } from '../provider.service';
import { ActivatedRoute } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { generateInventoryActionFormGroup, InventoryAction } from '../../models/inventory_action.model';
import { InventoryActionService } from '../../services/inventory-action.service';
import { MatDialog } from '@angular/material/dialog';
import { ClientMessageComponent } from '../../client/client-message/client-message.component';
import { AdminService } from '../../admin/services/admin.service';
import moment from 'moment';

interface IRequester {
  name: string;
  mail: string;
  phone: string;
}

@Component({
    selector: 'app-provider-pricing',
    templateUrl: './provider-pricing.component.html',
    styleUrl: './provider-pricing.component.scss',
    standalone: false
})
export class ProviderPricingComponent implements OnInit {

  public saving = signal(false);

  public company: any;

  public inventoryActionForPricing: InventoryAction;

  public requester: IRequester;

  public requiredProducts = [];

  private readonly fb = inject(FormBuilder);

  form: FormGroup = this.fb.group({
    id: this.fb.control(null),
    comment: this.fb.control(null),
    estimated_completion_date: this.fb.control(null, Validators.required),
    expiration_date: this.fb.control(null),
    inventory_action_products: this.fb.array([]),
    warehouse_id: this.fb.control(null, Validators.required),
  });

  private readonly providerService = inject(ProviderService);
  private readonly inventoryActionService = inject(InventoryActionService);
  private readonly route = inject(ActivatedRoute);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly matDialog = inject(MatDialog);
  private readonly adminService = inject(AdminService);

  constructor(
    private dialog: MatDialog,
  ) { }

  get requiredDeliveryDate() {
    return this.inventoryActionForPricing?.extra_params?.required_delivery_date;
  }

  ngOnInit() {
    this.company = this.adminService.local$.getValue();
    this.route.queryParams.subscribe((value) => {
      if (value.access_token) {
        // TODO: Handle not found case
        this.providerService.getPricing(value.access_token).subscribe((response: any) => {
          this.cdr.markForCheck();
          this.inventoryActionForPricing = response.body.inventory_action_for_pricing;
          this.form = generateInventoryActionFormGroup(this.inventoryActionForPricing, {
            include_status: true,
            include_comments: true,
            include_expiration_date: true,
          });
          this.requester = response.body.requester;
          this.requiredProducts = response.body.required_products;
        });
      }
    });
  }

  updateInventoryAction() {
    if (this.inventoryActionForPricing.finish) return;

    const formGroup = this.form;
    const inventoryActionRawData = formGroup.getRawValue();
    if (inventoryActionRawData.status.locked) return;

    const inventoryActionData = {
      ...inventoryActionRawData,
      inventory_action_products_attributes: inventoryActionRawData.inventory_action_products,
      inventory_action_products: undefined,
    };

    this.saving.set(true);
    if (inventoryActionData.id) {
      this.inventoryActionService.update(
        inventoryActionData.id,
        inventoryActionData,
      ).subscribe((inventoryAction) => {
        this.saving.set(false);
        if (!inventoryAction) return;

        (<FormArray> formGroup.get('inventory_action_products')).controls.forEach(
          (control, index1) => {
            if (control.value._destroy) {
              (<FormArray> formGroup.get('inventory_action_products')).removeAt(index1);
            } else {
              const inventoryActionProduct = (inventoryAction.inventory_action_products as any[]).find(
                (product) => product.product.code === control.getRawValue().code,
              );
              if (inventoryActionProduct) {
                control.patchValue(inventoryActionProduct, { emitEvent: false });
              }
            }
          },
        );

        this.cdr.detectChanges();
      });
    }
  }

  sendPricing() {
    if (!this.inventoryActionForPricing || (this.inventoryActionForPricing?.finish && !this.inventoryActionForPricing?.draft)) return;

    const title = 'Enviar Cotización';
    const action = this.form.value.extra_params?.answered ? 'reenviará' : 'enviará';
    this.dialog.open(ConfirmDialogComponent, {
        data: { title: title, body: `Esta acción ${action} la cotización al cliente. ¿Desea continuar?` },
      }).afterClosed().subscribe((result) => {
        if (result) {
          this.saving.set(true);
          this.inventoryActionService.update(
            this.form.value.id,
            { 
              draft: false,
              extra_params: { 
                ...this.form.value.extra_params,
                answered_at: moment(),
                answered: true
              },
              should_send_notification: true, 
            }
          ).subscribe(() => {
            this.saving.set(false);
            
            this.matDialog.open(ClientMessageComponent, {
              data: {
                message: 'Cotización enviada con éxito',
              },
              width: '100%',
              height: '100%',
              disableClose: true,
              maxWidth: '100%',
              maxHeight: '100vh',
              minHeight: '100vh',
              panelClass: 'client-message-dialog',
            }).afterClosed();
      
          });

        }
      });
  }
}
